/**
 * A screen's frame (K2a · K2b) — `/tv/<key>`, opened once on the stick and left. No sign-in: the
 * key is the screen's own. It asks for its frame every 15 seconds (every 5 while Watchtower holds),
 * says it is alive every minute, and keeps showing its last good frame when the network drops — with
 * a line saying so, rather than a blank screen in a food tent. Portrait is drawn portrait.
 *
 * What it plays is the published playlist's slides in turn, ten seconds each — a menu slide is the
 * area's board — or the board alone when no playlist is playing. `FrameView` is the same drawing the
 * proof (sig11) shows for every screen: the frames, not a description of them.
 */
import { useEffect, useState, type ReactNode } from 'react';
import './tv.css';

export interface Frame {
  label: string;
  orientation: 'landscape' | 'portrait';
  title: string;
  items: {
    name: string;
    price: string;
    soldOut: boolean;
    description: string | null;
    dietary: string[];
  }[];
  slides: { kind: 'menu' | 'words'; heading: string | null; body: string | null }[];
  hold: { says: string; minutesLeft: number | null } | null;
  /** Evacuation, a lost child, the all-clear: the whole screen, in Watchtower's words (sig09). */
  emergency: {
    state: 'evacuation' | 'lost_child' | 'all_clear';
    heading: string;
    words: string | null;
  } | null;
  refreshSeconds: number;
  says: string;
}

const HEARTBEAT_MS = 60_000;
const SLIDE_MS = 10_000;
/** Contains nuts, spicy: drawn as warnings; the other four as what suits. */
const WARNS = new Set(['nuts', 'spicy']);

/** One frame, drawn for the way its screen is mounted, at whatever size its stage is. */
export function FrameView({
  frame,
  slide = 0,
  proof = false,
  stale = null,
}: {
  frame: Frame;
  slide?: number;
  proof?: boolean;
  stale?: string | null;
}): ReactNode {
  const now = frame.slides.length ? frame.slides[slide % frame.slides.length] : undefined;
  const board = frame.items.length ? (
    <ul className="tv__items">
      {frame.items.map((i) => (
        <li key={i.name} className={`tv__item${i.soldOut ? ' tv__item--out' : ''}`}>
          <span className="tv__name">
            {i.name}
            {i.soldOut ? <span className="tv__out">SOLD OUT</span> : null}
            {i.dietary.map((d) => (
              <span key={d} className={`tv__diet${WARNS.has(d) ? ' tv__diet--warn' : ''}`}>
                {d.toUpperCase()}
              </span>
            ))}
            {i.description ? <span className="tv__desc">{i.description}</span> : null}
          </span>
          <span className="tv__price">{i.price}</span>
        </li>
      ))}
    </ul>
  ) : (
    <p className="tv__empty">{frame.says || 'The menu is coming.'}</p>
  );
  if (frame.emergency)
    return (
      <div className={`tv-stage tv-stage--${frame.orientation}${proof ? ' tv-stage--proof' : ''}`}>
        <main
          className={`tv tv--${frame.orientation} tv--emergency tv--${frame.emergency.state}`}
          aria-label={frame.label}
        >
          <section className="tv__emergency" role="alert">
            <h1>{frame.emergency.heading}</h1>
            {frame.emergency.words ? <p>{frame.emergency.words}</p> : null}
          </section>
          <span className="tv__brand">Apple Fest</span>
          {stale ? (
            <p className="tv__stale" role="status">
              {stale}
            </p>
          ) : null}
        </main>
      </div>
    );
  return (
    <div className={`tv-stage tv-stage--${frame.orientation}${proof ? ' tv-stage--proof' : ''}`}>
      <main className={`tv tv--${frame.orientation}`} aria-label={frame.label}>
        {frame.hold ? (
          <p className="tv__hold" role="alert">
            {frame.hold.says}
            {frame.hold.minutesLeft !== null ? (
              <span className="tv__clock">{String(frame.hold.minutesLeft)} min</span>
            ) : null}
          </p>
        ) : null}
        <header className="tv__head">
          <h1 className="tv__title">{frame.title}</h1>
          <span className="tv__brand">Apple Fest</span>
        </header>
        {now?.kind === 'words' ? (
          <section className="tv__words">
            {now.heading ? <h2>{now.heading}</h2> : null}
            {now.body ? <p>{now.body}</p> : null}
          </section>
        ) : (
          board
        )}
        {stale ? (
          <p className="tv__stale" role="status">
            {stale}
          </p>
        ) : null}
      </main>
    </div>
  );
}

export function Tv({ screenKey }: { screenKey: string }): ReactNode {
  const [frame, setFrame] = useState<Frame | null>(null);
  const [lost, setLost] = useState<'gone' | 'offline' | null>(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async (): Promise<void> => {
      let next = 15;
      try {
        const res = await fetch(`/api/public/screen/${encodeURIComponent(screenKey)}`, {
          credentials: 'omit',
          cache: 'no-store',
        });
        if (res.status === 404) setLost('gone');
        else if (res.ok) {
          const f = (await res.json()) as Frame;
          setFrame(f);
          setLost(null);
          next = f.refreshSeconds;
        } else setLost('offline');
      } catch {
        setLost('offline');
      }
      if (!stopped) timer = setTimeout(() => void ask(), next * 1000);
    };
    void ask();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [screenKey]);

  useEffect(() => {
    const beat = (): void => {
      void fetch(`/api/public/screen/${encodeURIComponent(screenKey)}/heartbeat`, {
        method: 'POST',
        credentials: 'omit',
      }).catch(() => undefined);
    };
    beat();
    const t = setInterval(beat, HEARTBEAT_MS);
    return () => {
      clearInterval(t);
    };
  }, [screenKey]);

  useEffect(() => {
    const t = setInterval(() => {
      setSlide((n) => n + 1);
    }, SLIDE_MS);
    return () => {
      clearInterval(t);
    };
  }, []);

  if (lost === 'gone' && !frame)
    return (
      <div className="tv-stage">
        <main className="tv">
          <p className="tv__empty">This screen’s address is not one CIDER knows.</p>
        </main>
      </div>
    );
  if (!frame)
    return (
      <div className="tv-stage">
        <main className="tv">
          <p className="tv__empty">Apple Fest</p>
        </main>
      </div>
    );
  return (
    <FrameView
      frame={frame}
      slide={slide}
      stale={
        lost === 'gone'
          ? 'This screen was retired. It shows its last menu.'
          : lost === 'offline'
            ? 'Not connected — showing the last menu it had.'
            : null
      }
    />
  );
}
